import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Heading,
  VStack,
  HStack,
  Icon,
  Text,
  Badge,
  Center,
  Spinner,
  Box,
} from '@chakra-ui/react';
import { FaHistory, FaChartLine, FaHome } from 'react-icons/fa';
import PriceHistoryChart from '../../../components/PriceHistoryChart';

const HousePrices = ({
  housePriceData,
  isLoading,
  postcode,
  validatedAddress,
  calculatedEstimatedPrice,
  propertyChartData,
  propertyAddress,
  sales,
  currentPropertySales,
  areaSales,
  chartData,
}) => {
  // Use the directly provided isLoading prop instead of looking for it in housePriceData

  return (
    <>
      {/* Price Overview Card */}
      {postcode && (
        <Card variant='outline' bg='green.50' h='100%'>
          <CardHeader>
            <HStack spacing={2}>
              <Icon as={FaChartLine} boxSize={5} color='brand.primary' />
              <Heading size='sm' color='brand.text'>
                Price Overview
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Center py={4}>
                <Spinner color='brand.primary' />
              </Center>
            ) : sales && sales?.length > 0 ? (
              <VStack align='stretch' spacing={4}>
                {propertyChartData && (
                  <Box p={4} bg='blue.50' borderRadius='md' borderLeft='4px' borderColor='blue.400'>
                    <HStack align='center' mb={2}>
                      <Text fontWeight='medium'>
                        Price Analysis for{' '}
                        {propertyAddress || propertyChartData.propertyAddress || 'This Property'}
                      </Text>
                      {validatedAddress?.flat_number && (
                        <Badge colorScheme='purple' ml={2}>
                          Flat {validatedAddress.flat_number.replace(/^flat\s+/i, '')}
                        </Badge>
                      )}
                    </HStack>
                    {/* Display number of sales found that match this specific property (including flat number) */}
                    <Text fontSize='sm' color='gray.600' mb={2}>
                      {currentPropertySales?.filter(sale => sale.price > 0)?.length > 0 
                        ? `Found ${currentPropertySales.filter(sale => sale.price > 0).length} sales matching this specific property`
                        : 'No previous sales found for this property'}
                    </Text>

                    {/* Display estimated current price */}
                    {calculatedEstimatedPrice > 0 && (
                      <Box
                        p={3}
                        mb={3}
                        bg='white'
                        borderRadius='md'
                        borderLeft='4px solid'
                        borderColor='blue.500'
                        boxShadow='sm'
                      >
                        <Text fontSize='sm' color='gray.600' fontWeight='semibold'>
                          Estimated Current Price:
                        </Text>
                        <HStack spacing={2} alignItems='center'>
                          <Text fontSize='xl' fontWeight='bold' color='blue.600'>
                            £
                            {calculatedEstimatedPrice.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </Text>
                          {(() => {
                            // Only proceed if we have all the required data
                            if (
                              !housePriceData ||
                              !housePriceData.regionPriceIndex ||
                              !housePriceData.regionPriceIndex.monthlyData ||
                              !propertyChartData ||
                              !propertyChartData.currentPropertyType
                            ) {
                              return null;
                            }

                            // Get the last month of regional data
                            const monthlyData = housePriceData.regionPriceIndex.monthlyData;
                            if (!monthlyData?.length) return null;

                            const lastMonthData = monthlyData[monthlyData?.length - 1];

                            // Map property types to the correct keys in the API response
                            const propertyTypeKeyMap = {
                              detached: 'averagePriceDetached',
                              'semi-detached': 'averagePriceSemiDetached',
                              terraced: 'averagePriceTerraced',
                              flat: 'averagePriceFlatMaisonette',
                            };

                            // Get the correct key for the current property type
                            const propertyTypeKey =
                              propertyTypeKeyMap[propertyChartData.currentPropertyType];
                            if (!propertyTypeKey) return null;

                            // Get the regional average price
                            const regionalAvgPrice = lastMonthData[propertyTypeKey];
                            if (!regionalAvgPrice) return null;

                            // Calculate the percentage difference
                            const diff =
                              ((calculatedEstimatedPrice - regionalAvgPrice) / regionalAvgPrice) *
                              100;
                            const formattedDiff = Math.abs(diff).toFixed(0);

                            return (
                              <Badge colorScheme={diff >= 0 ? 'green' : 'red'} variant='subtle'>
                                {diff >= 0 ? '+' : '-'}
                                {formattedDiff}% vs regional avg
                              </Badge>
                            );
                          })()}
                        </HStack>
                      </Box>
                    )}

                    {propertyChartData.propertyTrends ? (
                      <VStack spacing={3} align='stretch'>
                        {/* Only show first sale if it has a valid price (greater than 0) */}
                        {propertyChartData.propertyTrends.firstSale && propertyChartData.propertyTrends.firstSale.y > 0 ? (
                          <HStack>
                            <Text fontSize='sm' color='gray.600'>
                              {propertyChartData.propertyTrends.salesCount === 1
                                ? 'Sale Price:'
                                : 'First Sale:'}
                            </Text>
                            <Text fontSize='sm' fontWeight='medium'>
                              £{propertyChartData.propertyTrends.firstSale.y.toLocaleString()}
                              &nbsp;(
                              {new Date(
                                propertyChartData.propertyTrends.firstSale.x,
                              ).toLocaleDateString('en-GB', { year: 'numeric', month: 'short' })}
                              )
                            </Text>
                          </HStack>
                        ) : null}

                        {/* Only show Latest Sale if different from First Sale and has a valid price */}
                        {propertyChartData.propertyTrends.lastSale &&
                          propertyChartData.propertyTrends.lastSale.y > 0 &&
                          propertyChartData.propertyTrends.salesCount > 1 && (
                            <HStack>
                              <Text fontSize='sm' color='gray.600'>
                                Latest Sale:
                              </Text>
                              <Text fontSize='sm' fontWeight='medium'>
                                £{propertyChartData.propertyTrends.lastSale.y.toLocaleString()}
                                &nbsp;(
                                {new Date(
                                  propertyChartData.propertyTrends.lastSale.x,
                                ).toLocaleDateString('en-GB', { year: 'numeric', month: 'short' })}
                                )
                              </Text>
                            </HStack>
                          )}

                        {/* Show annual growth with comparison to regional average */}
                        {propertyChartData.propertyTrends.annualizedRate !== null &&
                          propertyChartData.propertyTrends.annualizedRate !== undefined &&
                          housePriceData &&
                          housePriceData.regionPriceIndex &&
                          housePriceData.regionPriceIndex.increases &&
                          housePriceData.regionPriceIndex.increases.byType &&
                          propertyChartData.currentPropertyType &&
                          housePriceData.regionPriceIndex.increases.byType[
                            propertyChartData.currentPropertyType
                          ] && (
                            <HStack alignItems='flex-start'>
                              <Text fontSize='sm' color='gray.600'>
                                Annual Growth:
                              </Text>
                              <VStack spacing={0} align='flex-start'>
                                <Text
                                  fontSize='sm'
                                  fontWeight='medium'
                                  color={
                                    propertyChartData.propertyTrends.annualizedRate >= 0
                                      ? 'green.600'
                                      : 'red.600'
                                  }
                                >
                                  {propertyChartData.propertyTrends.annualizedRate >= 0 ? '+' : ''}
                                  {(propertyChartData.propertyTrends.annualizedRate || 0).toFixed(
                                    1,
                                  )}
                                  % per year{' '}
                                  {(() => {
                                    const regionalRate =
                                      housePriceData.regionPriceIndex.increases.byType[
                                        propertyChartData.currentPropertyType
                                      ].annualized;
                                    const diff =
                                      ((propertyChartData.propertyTrends.annualizedRate -
                                        regionalRate) /
                                        regionalRate) *
                                      100;
                                    const formattedDiff = Math.abs(diff).toFixed(0);
                                    return (
                                      <Badge
                                        ml={1}
                                        colorScheme={diff >= 0 ? 'green' : 'red'}
                                        variant='subtle'
                                        fontSize='xs'
                                      >
                                        {diff >= 0 ? '+' : '-'}
                                        {formattedDiff}%
                                      </Badge>
                                    );
                                  })()}
                                </Text>
                                <Text fontSize='xs' color='gray.500'>
                                  {housePriceData.regionPriceIndex.region} average:
                                  {housePriceData.regionPriceIndex.increases.byType[
                                    propertyChartData.currentPropertyType
                                  ].annualized >= 0
                                    ? '+'
                                    : ''}
                                  {(
                                    housePriceData.regionPriceIndex.increases.byType[
                                      propertyChartData.currentPropertyType
                                    ].annualized || 0
                                  ).toFixed(1)}
                                  % per year
                                </Text>
                              </VStack>
                            </HStack>
                          )}

                        {/* Show annual growth without comparison when regional data isn't available */}
                        {propertyChartData.propertyTrends.annualizedRate !== null &&
                          propertyChartData.propertyTrends.annualizedRate !== undefined &&
                          (!housePriceData ||
                            !housePriceData.regionPriceIndex ||
                            !housePriceData.regionPriceIndex.increases ||
                            !housePriceData.regionPriceIndex.increases.byType ||
                            !propertyChartData.currentPropertyType ||
                            !housePriceData.regionPriceIndex.increases.byType[
                              propertyChartData.currentPropertyType
                            ]) && (
                            <HStack>
                              <Text fontSize='sm' color='gray.600'>
                                Annual Growth:
                              </Text>
                              <Text
                                fontSize='sm'
                                fontWeight='medium'
                                color={
                                  propertyChartData.propertyTrends.annualizedRate >= 0
                                    ? 'green.600'
                                    : 'red.600'
                                }
                              >
                                {propertyChartData.propertyTrends.annualizedRate >= 0 ? '+' : ''}
                                {(propertyChartData.propertyTrends.annualizedRate || 0).toFixed(1)}%
                                per year
                              </Text>
                            </HStack>
                          )}

                        {/* Show regional growth rate when there are less than 3 sales */}
                        {propertyChartData.propertyTrends.salesCount < 3 &&
                          propertyChartData.propertyTrends.salesCount > 0 &&
                          housePriceData &&
                          housePriceData.regionPriceIndex &&
                          housePriceData.regionPriceIndex.increases &&
                          housePriceData.regionPriceIndex.increases.byType &&
                          propertyChartData.currentPropertyType &&
                          housePriceData.regionPriceIndex.increases.byType[
                            propertyChartData.currentPropertyType
                          ] && (
                            <HStack>
                              <Text fontSize='sm' color='gray.600'>
                                Area Growth Rate:
                              </Text>
                              <Text
                                fontSize='sm'
                                fontWeight='medium'
                                color={
                                  housePriceData.regionPriceIndex.increases.byType[
                                    propertyChartData.currentPropertyType
                                  ].annualized >= 0
                                    ? 'green.600'
                                    : 'red.600'
                                }
                              >
                                {housePriceData.regionPriceIndex.increases.byType[
                                  propertyChartData.currentPropertyType
                                ].annualized >= 0
                                  ? '+'
                                  : ''}
                                {(
                                  housePriceData.regionPriceIndex.increases.byType[
                                    propertyChartData.currentPropertyType
                                  ].annualized || 0
                                ).toFixed(1)}
                                % per year
                              </Text>
                              <Badge colorScheme='blue' variant='outline' fontSize='xs'>
                                {housePriceData.regionPriceIndex.region}
                              </Badge>
                            </HStack>
                          )}

                        {/* Only show real sales count (with price > 0) */}
                        <HStack>
                          <Text fontSize='sm' color='gray.600'>
                            Number of Sales:
                          </Text>
                          <Text fontSize='sm' fontWeight='medium'>
                            {propertyChartData.propertyTrends.points ? 
                              propertyChartData.propertyTrends.points.filter(point => point.y > 0).length : 0}
                          </Text>
                        </HStack>
                        
                        {/* Show message when no actual sales data is available */}
                        {(!propertyChartData.propertyTrends.points || 
                          propertyChartData.propertyTrends.points.filter(point => point.y > 0).length === 0) && (
                          <>
                            <Text fontSize='sm' color='gray.600' fontStyle='italic' mb={2}>
                              No previous sales found for this property.
                            </Text>
                            
                            {/* Display estimated price based on regional averages when no sales data */}
                            {housePriceData && 
                             housePriceData.regionPriceIndex && 
                             housePriceData.regionPriceIndex.monthlyData && 
                             housePriceData.regionPriceIndex.monthlyData.length > 0 &&
                             propertyChartData.currentPropertyType && (
                              <>
                                {(() => {
                                  // Map property types to the correct keys in the API response
                                  const propertyTypeKeyMap = {
                                    detached: 'averagePriceDetached',
                                    'semi-detached': 'averagePriceSemiDetached',
                                    terraced: 'averagePriceTerraced',
                                    flat: 'averagePriceFlatMaisonette',
                                  };
                                  
                                  // Get the correct key for the current property type
                                  const propertyTypeKey = propertyTypeKeyMap[propertyChartData.currentPropertyType];
                                  if (!propertyTypeKey) return null;
                                  
                                  // Get the latest month data
                                  const lastMonthData = housePriceData.regionPriceIndex.monthlyData[
                                    housePriceData.regionPriceIndex.monthlyData.length - 1
                                  ];
                                  
                                  // Get the regional average price for this property type
                                  const regionalAvgPrice = lastMonthData[propertyTypeKey];
                                  if (!regionalAvgPrice) return null;
                                  
                                  return (
                                    <HStack mt={3}>
                                      <Text fontSize='sm' color='gray.600'>
                                        Estimated Price:
                                      </Text>
                                      <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                                        £{regionalAvgPrice.toLocaleString()}
                                      </Text>
                                      <Badge colorScheme='blue' variant='outline' fontSize='xs'>
                                        Based on regional average
                                      </Badge>
                                    </HStack>
                                  );
                                })()}
                              </>
                            )}
                          </>
                        )}

                        {/* For single sales, show estimated current price based on regional growth */}
                        {propertyChartData.propertyTrends.salesCount === 1 && 
                         propertyChartData.propertyTrends.firstSale && 
                         propertyChartData.propertyTrends.firstSale.y > 0 &&
                          housePriceData &&
                          housePriceData.regionPriceIndex &&
                          housePriceData.regionPriceIndex.increases &&
                          housePriceData.regionPriceIndex.increases.byType &&
                          propertyChartData.currentPropertyType &&
                          housePriceData.regionPriceIndex.increases.byType[
                            propertyChartData.currentPropertyType
                          ] &&
                          (() => {
                            const lastSaleDate = new Date(
                              propertyChartData.propertyTrends.firstSale.x,
                            );
                            const currentDate = new Date();

                            // Calculate years since last sale
                            const yearDiff = currentDate.getFullYear() - lastSaleDate.getFullYear();
                            const monthDiff = currentDate.getMonth() - lastSaleDate.getMonth();
                            const yearsSinceLastSale = yearDiff + monthDiff / 12;

                            // Get regional growth rate for this property type
                            const regionalAnnualGrowthRate =
                              housePriceData.regionPriceIndex.increases.byType[
                                propertyChartData.currentPropertyType
                              ].annualized / 100;

                            return (
                              <>
                                <HStack mt={3}>
                                  <Text fontSize='sm' color='gray.600'>
                                    Estimated Current Price:
                                  </Text>
                                  <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                                    £{calculatedEstimatedPrice.toLocaleString()}
                                  </Text>
                                  <Badge colorScheme='blue' variant='outline' fontSize='xs'>
                                    Based on regional growth
                                  </Badge>
                                </HStack>
                                <Text fontSize='sm' color='gray.600' fontStyle='italic' mt={2}>
                                  Only one sale recorded for this property. Estimate based on{' '}
                                  {(yearsSinceLastSale || 0).toFixed(1)} years of regional growth at{' '}
                                  {((regionalAnnualGrowthRate || 0) * 100).toFixed(1)}% per year.
                                </Text>
                              </>
                            );
                          })()}
                      </VStack>
                    ) : (
                      <Text fontSize='sm' color='gray.600'>
                        No detailed price analysis available for this property.
                      </Text>
                    )}
                  </Box>
                )}

                {/* Area Price Summary */}
                <Box
                  p={4}
                  mb={3}
                  bg='blue.50'
                  borderRadius='md'
                  borderLeft='4px solid'
                  borderColor='blue.400'
                  boxShadow='sm'
                >
                  <Text fontWeight='semibold' mb={3} color='blue.700'>
                    {propertyChartData && propertyChartData.currentPropertyType
                      ? `${
                          propertyChartData.currentPropertyType.charAt(0).toUpperCase() +
                          propertyChartData.currentPropertyType.slice(1)
                        } Properties in ${postcode}`
                      : 'Area Price Summary'}
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    {/* Calculate average price from same type properties */}
                    <HStack>
                      <Text fontSize='sm' color='gray.600'>
                        Average Price:
                      </Text>
                      {propertyChartData && propertyChartData.sameTypeStats ? (
                        <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                          £{propertyChartData.sameTypeStats.averagePrice.toLocaleString()}
                        </Text>
                      ) : (
                        <Text fontSize='sm' fontWeight='medium' color='gray.500'>
                          Not available
                        </Text>
                      )}
                    </HStack>
                    <HStack>
                      <Text fontSize='sm' color='gray.600'>
                        Total Sales:
                      </Text>
                      {propertyChartData && propertyChartData.sameTypeStats ? (
                        <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                          {propertyChartData.sameTypeStats.count}
                        </Text>
                      ) : (
                        <Text fontSize='sm' fontWeight='medium' color='gray.500'>
                          {areaSales?.length || 'Not available'}
                        </Text>
                      )}
                    </HStack>
                    <HStack>
                      <Text fontSize='sm' color='gray.600'>
                        Min Price:
                      </Text>
                      {propertyChartData && propertyChartData.sameTypeStats ? (
                        <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                          £{propertyChartData.sameTypeStats.minPrice.toLocaleString()}
                        </Text>
                      ) : (
                        <Text fontSize='sm' fontWeight='medium' color='gray.500'>
                          Not available
                        </Text>
                      )}
                    </HStack>
                    <HStack>
                      <Text fontSize='sm' color='gray.600'>
                        Max Price:
                      </Text>
                      {propertyChartData && propertyChartData.sameTypeStats ? (
                        <Text fontSize='sm' fontWeight='medium' color='blue.600'>
                          £{propertyChartData.sameTypeStats.maxPrice.toLocaleString()}
                        </Text>
                      ) : (
                        <Text fontSize='sm' fontWeight='medium' color='gray.500'>
                          Not available
                        </Text>
                      )}
                    </HStack>
                  </SimpleGrid>
                </Box>

                {/* Regional Price Index Section */}
                {console.log(
                  'Rendering Regional Price Index section, exists:',
                  !!housePriceData?.regionPriceIndex,
                )}
                {housePriceData?.regionPriceIndex && (
                  <Box
                    p={4}
                    bg='green.50'
                    borderRadius='md'
                    borderLeft='4px solid'
                    borderColor='green.500'
                    mt={3}
                    boxShadow='sm'
                  >
                    <HStack justifyContent='space-between' mb={2}>
                      <Text fontWeight='medium'>Regional Price Trends</Text>
                      <Badge colorScheme='green'>
                        {housePriceData.regionPriceIndex.region
                          ? housePriceData.regionPriceIndex.region.charAt(0).toUpperCase() +
                            housePriceData.regionPriceIndex.region.slice(1)
                          : 'London'}
                      </Badge>
                    </HStack>

                    {/* Period Information */}
                    <Text fontSize='xs' mb={2} color='gray.600'>
                      Data period: {housePriceData.regionPriceIndex.period?.start || '2021-12'} to{' '}
                      {housePriceData.regionPriceIndex.period?.end || '2022-12'}(
                      {housePriceData.regionPriceIndex.period?.months || '12'} months)
                    </Text>
                    <Text fontSize='xs' mb={3} color='gray.600' fontStyle='italic'>
                      Annual values represent the average yearly growth rate over the entire period
                    </Text>

                    {/* Average Price Increase */}
                    <Box mb={4} p={3} bg='white' borderRadius='md' boxShadow='xs'>
                      <Text fontSize='sm' fontWeight='semibold' mb={2} color='green.700'>
                        Average Price Change
                      </Text>
                      <SimpleGrid columns={3} spacing={3}>
                        <Box>
                          <Text fontSize='xs' color='gray.600'>
                            Total Change
                          </Text>
                          <Text fontSize='sm' fontWeight='medium' color='green.600'>
                            £
                            {housePriceData.regionPriceIndex.increases?.averagePrice?.absolute?.toLocaleString(
                              undefined,
                              { maximumFractionDigits: 0 },
                            ) || 'N/A'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize='xs' color='gray.600'>
                            Total %
                          </Text>
                          <Text fontSize='sm' fontWeight='medium' color='green.600'>
                            {(
                              housePriceData.regionPriceIndex.increases?.averagePrice?.percentage ||
                              0
                            ).toFixed(1)}
                            %
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize='xs' color='gray.600'>
                            Annual %
                          </Text>
                          <Text fontSize='sm' fontWeight='bold' color='green.600'>
                            {(
                              housePriceData.regionPriceIndex.increases?.averagePrice?.annualized ||
                              0
                            ).toFixed(1)}
                            %
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </Box>

                    {/* Price Changes by Property Type */}
                    <Box p={3} bg='white' borderRadius='md' boxShadow='xs'>
                      <Text fontSize='sm' fontWeight='semibold' mb={2} color='green.700'>
                        Annual Price Change by Property Type
                      </Text>
                      <SimpleGrid columns={2} spacing={3}>
                        {housePriceData.regionPriceIndex.increases?.byType?.detached && (
                          <HStack
                            justify='space-between'
                            p={1}
                            bg={
                              housePriceData.regionPriceIndex.increases.byType.detached.annualized >
                              0
                                ? 'green.50'
                                : 'red.50'
                            }
                            borderRadius='sm'
                          >
                            <Text fontSize='sm' color='gray.700'>
                              Detached:
                            </Text>
                            <Text
                              fontSize='sm'
                              fontWeight='medium'
                              color={
                                housePriceData.regionPriceIndex.increases.byType.detached
                                  .annualized > 0
                                  ? 'green.600'
                                  : 'red.600'
                              }
                            >
                              {(
                                housePriceData.regionPriceIndex.increases.byType.detached
                                  .annualized || 0
                              ).toFixed(1)}
                              %
                            </Text>
                          </HStack>
                        )}
                        {housePriceData.regionPriceIndex.increases?.byType?.semiDetached && (
                          <HStack
                            justify='space-between'
                            p={1}
                            bg={
                              housePriceData.regionPriceIndex.increases.byType.semiDetached
                                .annualized > 0
                                ? 'green.50'
                                : 'red.50'
                            }
                            borderRadius='sm'
                          >
                            <Text fontSize='sm' color='gray.700'>
                              Semi-Detached:
                            </Text>
                            <Text
                              fontSize='sm'
                              fontWeight='medium'
                              color={
                                housePriceData.regionPriceIndex.increases.byType.semiDetached
                                  .annualized > 0
                                  ? 'green.600'
                                  : 'red.600'
                              }
                            >
                              {(
                                housePriceData.regionPriceIndex.increases.byType.semiDetached
                                  .annualized || 0
                              ).toFixed(1)}
                              %
                            </Text>
                          </HStack>
                        )}
                        {housePriceData.regionPriceIndex.increases?.byType?.terraced && (
                          <HStack
                            justify='space-between'
                            p={1}
                            bg={
                              housePriceData.regionPriceIndex.increases.byType.terraced.annualized >
                              0
                                ? 'green.50'
                                : 'red.50'
                            }
                            borderRadius='sm'
                          >
                            <Text fontSize='sm' color='gray.700'>
                              Terraced:
                            </Text>
                            <Text
                              fontSize='sm'
                              fontWeight='medium'
                              color={
                                housePriceData.regionPriceIndex.increases.byType.terraced
                                  .annualized > 0
                                  ? 'green.600'
                                  : 'red.600'
                              }
                            >
                              {(
                                housePriceData.regionPriceIndex.increases.byType.terraced
                                  .annualized || 0
                              ).toFixed(1)}
                              %
                            </Text>
                          </HStack>
                        )}
                        {housePriceData.regionPriceIndex.increases?.byType?.flat && (
                          <HStack
                            justify='space-between'
                            p={1}
                            bg={
                              housePriceData.regionPriceIndex.increases.byType.flat.annualized > 0
                                ? 'green.50'
                                : 'red.50'
                            }
                            borderRadius='sm'
                          >
                            <Text fontSize='sm' color='gray.700'>
                              Flat/Maisonette:
                            </Text>
                            <Text
                              fontSize='sm'
                              fontWeight='medium'
                              color={
                                housePriceData.regionPriceIndex.increases.byType.flat.annualized > 0
                                  ? 'green.600'
                                  : 'red.600'
                              }
                            >
                              {(
                                housePriceData.regionPriceIndex.increases.byType.flat.annualized ||
                                0
                              ).toFixed(1)}
                              %
                            </Text>
                          </HStack>
                        )}
                      </SimpleGrid>
                    </Box>

                    {/* Latest Average Prices */}
                    {housePriceData.regionPriceIndex.monthlyData &&
                      housePriceData.regionPriceIndex.monthlyData?.length > 0 && (
                        <Box mt={3}>
                          <Text fontSize='sm' fontWeight='semibold' mb={1}>
                            Latest Average Prices (
                            {
                              housePriceData.regionPriceIndex.monthlyData[
                                housePriceData.regionPriceIndex.monthlyData?.length - 1
                              ]?.date
                            }
                            )
                          </Text>
                          <SimpleGrid columns={2} spacing={2}>
                            <HStack>
                              <Text fontSize='xs' color='gray.600'>
                                Overall:
                              </Text>
                              <Text fontSize='xs' fontWeight='medium'>
                                £
                                {housePriceData.regionPriceIndex.monthlyData[
                                  housePriceData.regionPriceIndex.monthlyData.length - 1
                                ]?.averagePrice?.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </Text>
                            </HStack>
                            <HStack>
                              <Text fontSize='xs' color='gray.600'>
                                Detached:
                              </Text>
                              <Text fontSize='xs' fontWeight='medium'>
                                £
                                {housePriceData.regionPriceIndex.monthlyData[
                                  housePriceData.regionPriceIndex.monthlyData.length - 1
                                ]?.averagePriceDetached?.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </Text>
                            </HStack>
                            <HStack>
                              <Text fontSize='xs' color='gray.600'>
                                Semi-Detached:
                              </Text>
                              <Text fontSize='xs' fontWeight='medium'>
                                £
                                {housePriceData.regionPriceIndex.monthlyData[
                                  housePriceData.regionPriceIndex.monthlyData.length - 1
                                ]?.averagePriceSemiDetached?.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </Text>
                            </HStack>
                            <HStack>
                              <Text fontSize='xs' color='gray.600'>
                                Terraced:
                              </Text>
                              <Text fontSize='xs' fontWeight='medium'>
                                £
                                {housePriceData.regionPriceIndex.monthlyData[
                                  housePriceData.regionPriceIndex.monthlyData.length - 1
                                ]?.averagePriceTerraced?.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </Text>
                            </HStack>
                            <HStack>
                              <Text fontSize='xs' color='gray.600'>
                                Flat/Maisonette:
                              </Text>
                              <Text fontSize='xs' fontWeight='medium'>
                                £
                                {housePriceData.regionPriceIndex.monthlyData[
                                  housePriceData.regionPriceIndex.monthlyData.length - 1
                                ]?.averagePriceFlat?.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </Text>
                            </HStack>
                          </SimpleGrid>
                        </Box>
                      )}
                  </Box>
                )}
              </VStack>
            ) : (
              <Text color='gray.500'>No price data available for this area</Text>
            )}
          </CardBody>
        </Card>
      )}

      {/* Property Price History Card */}
      {currentPropertySales?.length > 0 && (
        <Card variant='outline' bg='green.50' h='100%'>
          <CardHeader>
            <HStack spacing={2} justify='space-between' align='center' width='100%'>
              <HStack spacing={2}>
                <Icon as={FaHome} boxSize={5} color='brand.primary' />
                <Heading size='sm' color='brand.text'>
                  Property Price History
                </Heading>
              </HStack>
              <Badge colorScheme='yellow' fontSize='xs'>
                {propertyAddress}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Center py={4}>
                <Spinner color='brand.primary' />
              </Center>
            ) : propertyChartData ? (
              <VStack spacing={4} align='stretch'>
                <Box>
                  <PriceHistoryChart data={propertyChartData} />
                </Box>
              </VStack>
            ) : (
              <Text color='gray.500'>No previous sales data available for this property</Text>
            )}
          </CardBody>
        </Card>
      )}

      {/* Area Price Trends Card */}
      {sales && sales.length > 0 && (
        <Card variant='outline' bg='green.50' h='100%'>
          <CardHeader>
            <HStack justify='space-between' align='center' width='100%'>
              <HStack spacing={2}>
                <Icon as={FaChartLine} boxSize={5} color='brand.primary' />
                <Heading size='sm' color='brand.text'>
                  {validatedAddress?.locality
                    ? `${validatedAddress.locality} Area Prices`
                    : 'Area Price Trends'}
                </Heading>
              </HStack>
              <Badge colorScheme='blue' fontSize='xs'>
                {postcode}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Center py={4}>
                <Spinner color='brand.primary' />
              </Center>
            ) : chartData ? (
              <Box>
                <PriceHistoryChart data={chartData} />
              </Box>
            ) : (
              <Text color='gray.500'>Insufficient data for chart visualization</Text>
            )}
          </CardBody>
        </Card>
      )}

      {/* Previous Sales List */}
      {sales && sales.length > 0 && (
        <Card variant='outline' bg='green.50' h='100%'>
          <CardHeader>
            <HStack justify='space-between' align='center' width='100%'>
              <HStack spacing={2}>
                <Icon as={FaHistory} boxSize={5} color='brand.primary' />
                <Heading size='sm' color='brand.text'>
                  {(() => {
                    // Extract street information
                    let streetName = '';

                    // Use validatedAddress if available
                    if (validatedAddress && validatedAddress.street) {
                      streetName = validatedAddress.street;
                    } else {
                      // Fallback to extracting from sales data
                      const recentSale = [...sales].sort(
                        (a, b) => new Date(b.date) - new Date(a.date),
                      )[0];
                      streetName = recentSale?.address?.street || '';
                    }

                    // Create title
                    if (streetName) {
                      return `Sales on ${streetName}`;
                    } else {
                      return 'Property Sales History';
                    }
                  })()}
                </Heading>
              </HStack>
              <Badge colorScheme='green' fontSize='xs'>
                {postcode}
              </Badge>
            </HStack>
          </CardHeader>

          <CardBody>
            {isLoading ? (
              <Center py={4}>
                <Spinner color='brand.primary' />
              </Center>
            ) : (
              <VStack spacing={4} align='stretch'>
                {/* Use the existing state variables that are already set in useEffect */}
                {/* First show current property sales */}
                {currentPropertySales.filter(sale => sale.price > 0).length > 0 && (
                  <Box>
                    <Text fontWeight='bold' mb={2} color='brand.primary'>
                      Current Property Sales
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 1 }} spacing={3}>
                      {currentPropertySales
                        .filter(sale => sale.price > 0) // Filter out sales with £0 price
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((sale, index) => (
                          <VStack
                            key={`current-sale-${index}`}
                            align='stretch'
                            spacing={1}
                            bg='yellow.50'
                            p={3}
                            borderRadius='md'
                            borderLeft='4px solid'
                            borderColor='yellow.400'
                          >
                            <HStack justify='space-between'>
                              <Text fontSize='sm' color='brand.text' fontWeight='bold'>
                                {validatedAddress
                                  ? `${
                                      validatedAddress.flat_number
                                        ? `Flat ${validatedAddress.flat_number.replace(
                                            /^flat\s+/i,
                                            '',
                                          )}, `
                                        : ''
                                    }${validatedAddress.street_number || ''} ${
                                      validatedAddress.street || ''
                                    }`.trim()
                                  : `${sale.address?.saon ? `${sale.address.saon}, ` : ''}${
                                      sale.address?.paon || ''
                                    } ${sale.address?.street || ''}`.trim()}
                                <Badge ml={2} colorScheme='yellow' fontSize='xs'>
                                  Current Property
                                </Badge>
                              </Text>
                              <Badge fontSize='xs' colorScheme='green'>
                                £{sale.price.toLocaleString()}
                              </Badge>
                            </HStack>
                            <HStack justify='space-between'>
                              <Text fontSize='xs' color='gray.600'>
                                {new Date(sale.date).toLocaleDateString('en-GB', {
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </Text>
                              <Badge fontSize='xs' colorScheme='blue'>
                                {sale.propertyType || 'Unknown'}
                              </Badge>
                            </HStack>
                          </VStack>
                        ))}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Then show other sales on the street */}
                {areaSales?.filter(sale => sale.price > 0)?.length > 0 && (
                  <Box mt={currentPropertySales?.filter(sale => sale.price > 0)?.length > 0 ? 4 : 0}>
                    <Text fontWeight='bold' mb={2} color='brand.primary'>
                      Other Sales on this Street
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 1 }} spacing={3}>
                      {areaSales
                        .filter(sale => sale.price > 0) // Filter out sales with £0 price
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 8) // Limit to 8 sales
                        .map((sale, index) => (
                          <VStack
                            key={`area-sale-${index}`}
                            align='stretch'
                            spacing={1}
                            bg='white'
                            p={3}
                            borderRadius='md'
                          >
                            <HStack justify='space-between'>
                              <Text fontSize='sm' color='brand.text'>
                                {`${sale.address?.saon ? `${sale.address.saon}, ` : ''}${
                                  sale.address?.paon || ''
                                } ${sale.address?.street || ''}`.trim()}
                              </Text>
                              <Badge fontSize='xs' colorScheme='green'>
                                £{sale.price.toLocaleString()}
                              </Badge>
                            </HStack>
                            <HStack justify='space-between'>
                              <Text fontSize='xs' color='gray.600'>
                                {new Date(sale.date).toLocaleDateString('en-GB', {
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </Text>
                              <Badge fontSize='xs' colorScheme='blue'>
                                {sale.propertyType || 'Unknown'}
                              </Badge>
                            </HStack>
                          </VStack>
                        ))}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Show message if no sales data */}
                {currentPropertySales?.length === 0 && areaSales.length === 0 && (
                  <Text color='gray.500'>No sales data available for this area</Text>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>
      )}

      {/* Show a placeholder card if no data is available */}
      {!isLoading && (!sales || sales?.length === 0) && (
        <Card variant='outline' bg='gray.50' h='100%'>
          <CardHeader>
            <HStack spacing={2}>
              <Icon as={FaHome} boxSize={5} color='gray.400' />
              <Heading size='sm' color='gray.500'>
                Property Price Data
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <Center py={8}>
              <Text color='gray.500'>No price data available for this property</Text>
            </Center>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default HousePrices;
